// libs
import React, { memo, useEffect, useRef, useState } from 'react';

// styles
import { Button, Container, Content, ExpanderIcon } from './styles';

// defs
interface ICollapsibleProps {
  header: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  headerClassName?: string;
  childrenClassName?: string;
  onChange?: (isExpanded: boolean) => void;
  isExpanded?: boolean;
  CustomExpander?: ({ isExpanded }: { isExpanded: boolean }) => React.ReactNode;
  fixedContentHeight?: boolean;
  onTransitionEnd?: VoidFunction;
}

const EMPTY_FUNC = () => {
  /** DO NOTHING */
};

function Collapsible({
  header,
  children,
  className,
  headerClassName = '',
  childrenClassName = '',
  onChange,
  isExpanded = false,
  CustomExpander,
  fixedContentHeight = true,
  onTransitionEnd = EMPTY_FUNC,
}: ICollapsibleProps) {
  const [showContent, setShowContent] = useState(isExpanded);
  const [height, setHeight] = useState<string | number>(isExpanded ? 'auto' : 0);
  const contentElement = useRef<HTMLDivElement>(null);

  const expanderCLName = `cp-expander ${showContent ? 'expanded' : ''}`;
  const headerCLName = `cp-header ${headerClassName}`;
  const contentCLName = `cp-content ${childrenClassName} ${!showContent ? 'collapsed' : ''}`;

  const onClick = () => {
    setShowContent((currShowContent) => {
      const newShowContent = !currShowContent;
      onChange && onChange(newShowContent);
      return newShowContent;
    });
  };

  // to set the scrollHeight once the content is rendered
  useEffect(() => {
    // @ts-ignore
    const newHeight = fixedContentHeight ? contentElement.current.scrollHeight : 'auto';
    setHeight(showContent ? newHeight : 0);
  }, [fixedContentHeight, showContent]);

  // if Props modified
  useEffect(() => {
    setShowContent(isExpanded);
  }, [isExpanded]);

  return (
    <Container className={className} data-test="wrapperContent">
      <Button
        className={headerCLName}
        onClick={onClick}
        aria-expanded={showContent}
        data-test="headerContent"
      >
        {header}
        {CustomExpander ? (
          <CustomExpander isExpanded={showContent} data-test="custom-expander" />
        ) : (
          <ExpanderIcon className={expanderCLName} data-test="default-expander" />
        )}
      </Button>

      <Content
        ref={contentElement}
        style={{ height }}
        className={contentCLName}
        data-test="childContent"
        onTransitionEnd={onTransitionEnd}
      >
        {children}
      </Content>
    </Container>
  );
}

export default memo(Collapsible);
